import { Lightning, Router } from '@lightningjs/sdk'

import SpeechComp from '../../components/speechComp/speechComp'
import { getColor, grey300, grey50, grey700, grey960, transparent } from '../../constants/colors'
import { UniNeueRegular } from '../../constants/fonts'
import { WIDGETS_Z_INDEX } from '../../constants/general'
import { isLowEndDevice } from '../../utils/info'
import { menuCollapsed, menuUncollapsed } from '../../utils/menuUtils'

const MENU_ICON_FONT_SIZE = 32
const MENU_ITEM_COLLAPSED = 84
const MENU_ITEM_UNCOLLAPSED = 281
export default class MenuItem extends SpeechComp {
  private _route: string
  private _listeners: any
  private _isSelected = false
  private readonly _enableSmooth = !isLowEndDevice()
  private _hasIconLoaded: boolean = false
  private _itemIndex: number
  private _totalItems: number

  static _template() {
    return {
      h: MENU_ITEM_COLLAPSED,
      w: MENU_ITEM_COLLAPSED,
      rect: true,
      collision: true,
      zIndex: WIDGETS_Z_INDEX + 1,
      color: transparent,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: 16,
      },
      flex: {
        direction: 'row',
        alignItems: 'center',
      },
      Icon: {
        w: MENU_ICON_FONT_SIZE,
        h: MENU_ICON_FONT_SIZE,
        color: getColor(grey300),
        flexItem: {
          marginLeft: 26,
        },
      },
      Label: {
        y: 4,
        visible: false,
        text: {
          text: '',
          fontSize: MENU_ICON_FONT_SIZE,
          textColor: getColor(grey300),
          fontFace: UniNeueRegular,
        },
        flexItem: {
          marginLeft: 20,
        },
      },
    }
  }

  get icon() {
    return this.tag('Icon')
  }

  get label() {
    return this.tag('Label')
  }

  set itemText(v) {
    this.label.text = v
  }

  set itemIcon(v) {
    this.icon.patch({
      texture: Lightning.Tools.getSvgTexture(v, MENU_ICON_FONT_SIZE, MENU_ICON_FONT_SIZE),
    })
  }

  get route() {
    return this._route
  }

  set route(v) {
    this._route = v
  }

  get speechText(): string {
    return this.label?.text?.text + ' . . Item ' + (this.itemIndex + 1) + ' of  ' + this.totalItems
  }

  set isSelected(v: boolean) {
    this._isSelected = v
    if (this._isSelected && this._hasIconLoaded) {
      this.setSelectedState()
    }
  }

  get itemIndex() {
    return this._itemIndex
  }

  set itemIndex(v: number) {
    this._itemIndex = v
  }

  get totalItems() {
    return this._totalItems
  }

  set totalItems(v: number) {
    this._totalItems = v
  }

  _init() {
    this._listeners = {
      menuCollapsed: () => {
        this.patch({
          smooth: {
            w: MENU_ITEM_COLLAPSED,
          },
          Label: {
            visible: false,
          },
        })
      },
      menuUncollapsed: () => {
        this.patch({
          smooth: {
            w: MENU_ITEM_UNCOLLAPSED,
          },
          Label: {
            visible: true,
          },
        })
      },
    }

    this.icon.on('txLoaded', () => {
      this._hasIconLoaded = true

      if (this._isSelected) {
        this.setSelectedState()
      }
    })
  }

  _attach() {
    ;[menuCollapsed, menuUncollapsed].forEach((event) => {
      this.application.on(event, this._listeners[event])
    })
  }

  _detach() {
    ;[menuCollapsed, menuUncollapsed].forEach((event) => {
      this.application.off(event)
    })
  }

  _inactive() {
    this._hasIconLoaded = false
    this.patch({ color: transparent })
  }

  _focus() {
    this.focusItem(true)
    this.speak()
  }

  _unfocus() {
    this.focusItem(false)
  }

  selectItem() {
    if (Router.getActiveHash() === this.route) {
      Router.reload()
      return
    }

    if (this.route) {
      Router.navigate(this.route)
    }

    this._isSelected = true
  }

  unselectItem() {
    this.focusItem((this._isSelected = false))
  }

  focusItem(isFocused: boolean) {
    if (!this._hasIconLoaded) {
      return
    }

    const bgColor = isFocused
      ? getColor(grey50)
      : this._isSelected
      ? getColor(grey700)
      : transparent

    const color = isFocused
      ? getColor(grey960)
      : this._isSelected
      ? getColor(grey50)
      : getColor(grey300)

    this.patch({
      ...(this._enableSmooth
        ? {
            smooth: {
              color: bgColor,
            },
          }
        : { color: bgColor }),
      Icon: {
        color: color,
      },
      Label: {
        text: {
          textColor: color,
        },
      },
    })
  }

  setSelectedState() {
    this.patch({
      color: getColor(grey700),
      Icon: {
        color: getColor(grey50),
      },
      Label: {
        text: {
          textColor: getColor(grey50),
        },
      },
    })
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this._unfocus()
  }

  _handleClick() {
    this.fireAncestors('$onMenuItemClicked')
  }
}
